import React, { useState } from 'react';
import { AgentQuestionMessage as AgentQuestionMessageType } from 'types/chat.types';
import reasoningService from 'services/reasoningService';

interface AgentQuestionMessageProps {
  message: AgentQuestionMessageType;
  onAnswered?: () => void;
}

const AgentQuestionMessage: React.FC<AgentQuestionMessageProps> = ({ message, onAnswered }) => {
  const { questions, jobId, timestamp } = message;
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleOptionSelect = (idx: number, option: string) => {
    setAnswers((prev) => ({ ...prev, [idx]: option }));
  };

  const handleTextChange = (idx: number, value: string) => {
    setAnswers((prev) => ({ ...prev, [idx]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formattedAnswers = questions.map((q, idx) => ({
        question: q.question,
        answer: answers[idx] || '',
      }));
      await reasoningService.submitAnswers(jobId, formattedAnswers);
      setSubmitted(true);
      onAnswered?.();
    } catch (err) {
      console.error('Failed to submit answers:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const allAnswered = questions.every((_, idx) => answers[idx]?.trim());

  if (submitted) {
    return (
      <div className="self-start flex flex-col w-full max-w-2xl">
        <div className="bg-gray-50 border border-gray-200 px-4 py-3 rounded-2xl rounded-bl-md">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm text-gray-700">Answers submitted — classification resuming</span>
          </div>
        </div>
        <div className="text-[10px] text-gray-400 mt-1 ml-2">
          {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    );
  }

  return (
    <div className="self-start flex flex-col w-full max-w-2xl">
      <div className="bg-white border border-gray-200 px-4 py-4 rounded-2xl rounded-bl-md shadow-sm">
        <div className="flex items-center gap-2.5 mb-3 pb-2.5 border-b border-gray-100">
          <div className="w-6 h-6 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
            <svg className="w-3.5 h-3.5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-gray-800">
            Clarification needed to classify accurately
          </p>
        </div>

        <div className="space-y-4">
          {questions.map((q, idx) => (
            <div key={idx} className="space-y-2">
              <p className="text-[13px] font-medium text-gray-800">{q.question}</p>
              {q.context && (
                <p className="text-xs text-gray-500">{q.context}</p>
              )}

              {q.options && q.options.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {q.options.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleOptionSelect(idx, option)}
                      className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                        answers[idx] === option
                          ? 'bg-teal-50 border-teal-400 text-teal-800'
                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-teal-300'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              ) : (
                <input
                  type="text"
                  value={answers[idx] || ''}
                  onChange={(e) => handleTextChange(idx, e.target.value)}
                  placeholder="Type your answer…"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-400"
                />
              )}
            </div>
          ))}
        </div>

        <button
          onClick={handleSubmit}
          disabled={!allAnswered || submitting}
          className={`mt-4 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            allAnswered && !submitting
              ? 'bg-teal-600 text-white hover:bg-teal-700'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {submitting ? 'Submitting…' : 'Submit Answers'}
        </button>
      </div>
      <div className="text-[10px] text-gray-400 mt-1 ml-2">
        {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
};

export default AgentQuestionMessage;
